import * as React from 'react';
import {
    Alert, Avatar, Box, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Pagination,
    Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { getSubscribers, getSubscriptionEvents } from '../Api';
import { SubscriberDto, SubscribersPageDto, SubscriptionEventItemDto } from '../models/dtos/UserPremiumAdminDto';
import SubscriptionTierChip from './SubscriptionTierChip';
import { EVENT_ICON, storeIcon } from './UserSubscriptionsCard';

const PAGE_SIZE = 20;
const TIERS = ['ALL', 'PLUS', 'MAX'] as const;

/** Where a subscription is billed (`shop`); BETUP = granted from the dashboard. */
const SHOP_FILTERS = [
    { value: 'ALL', label: 'All stores' },
    { value: 'APPLE', label: 'App Store' },
    { value: 'GOOGLE', label: 'Google Play' },
    { value: 'BETUP', label: 'BETUP grant' },
];

/** Store sandbox / test (and BETUP grants) vs real payments. */
export const TEST_FILTERS = [
    { value: 'ALL', label: 'Real + test' },
    { value: 'REAL', label: 'Real only' },
    { value: 'TEST', label: 'Test only' },
];

function formatDay(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleDateString();
}

function formatDateTime(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleString();
}

function formatPrice(micros: number | null, currency: string | null): string {
    if (micros == null) return '—';
    const amount = (micros / 1_000_000).toFixed(2);
    return currency === 'USD' ? `$${amount}` : `${currency ?? ''} ${amount}`.trim();
}

/**
 * Marketing › Subscriptions › Subscribers (analytics SubscriptionMonitorService): the live subscriptions, newest
 * first, by tier, store and real / test (the tier counts follow the other two), with where they're billed and how they
 * go on. A row opens its events; the user icon opens the
 * player's Premium tab.
 */
export default function SubscribersCard() {
    const navigate = useNavigate();
    const [tier, setTier] = React.useState<typeof TIERS[number]>('ALL');
    const [shop, setShop] = React.useState('ALL');
    const [test, setTest] = React.useState('ALL');
    const [page, setPage] = React.useState(1);
    const [data, setData] = React.useState<SubscribersPageDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);
    const [eventsOf, setEventsOf] = React.useState<SubscriberDto | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getSubscribers(tier, shop, test, page - 1, PAGE_SIZE).then(result => {
            if (cancelled) return;
            setLoading(false);
            setFailed(result == null);
            if (result) setData(result);
        });
        return () => { cancelled = true; };
    }, [tier, shop, test, page]);

    const counts = data?.countsByTier ?? {};
    const total = (counts.PLUS ?? 0) + (counts.MAX ?? 0);
    const pages = Math.max(1, Math.ceil((data?.totalItems ?? 0) / PAGE_SIZE));

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                        <GroupsIcon color="primary" />
                        <Typography variant="h6">Subscribers</Typography>
                        {loading && <CircularProgress size={18} />}
                        <Box sx={{ flexGrow: 1 }} />
                        <TextField select size="small" label="Store" value={shop} sx={{ width: 150 }}
                            onChange={e => { setShop(e.target.value); setPage(1); }}>
                            {SHOP_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label="Payments" value={test} sx={{ width: 140 }}
                            onChange={e => { setTest(e.target.value); setPage(1); }}>
                            {TEST_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                        </TextField>
                        <ToggleButtonGroup size="small" exclusive value={tier}
                            onChange={(_, value) => { if (value) { setTier(value); setPage(1); } }}>
                            {TIERS.map(t => (
                                <ToggleButton key={t} value={t}>
                                    {t === 'ALL' ? `All · ${total}` : `${t === 'PLUS' ? 'Plus' : 'Max'} · ${counts[t] ?? 0}`}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                    {failed && <Alert severity="error">Failed to load the subscribers.</Alert>}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Player</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Since</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.items.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center">No live subscriptions</TableCell></TableRow>
                            )}
                            {data?.items.map(s => (
                                <TableRow key={s.subscriptionId} hover sx={{ cursor: 'pointer' }} onClick={() => setEventsOf(s)}>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Avatar src={s.photoUrl ?? undefined} sx={{ width: 28, height: 28 }} />
                                            <Box>
                                                <Typography variant="body2">{s.userName ?? '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">#{s.userId}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={0.5} alignItems="center">
                                            <SubscriptionTierChip tier={s.tier} />
                                            {s.nextPlan && <Chip size="small" variant="outlined" label={`→ ${s.nextPlan}`} />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={0.5} alignItems="center">
                                            {storeIcon(s.shop)}
                                            <Typography variant="body2">{s.shop}</Typography>
                                            {s.test && <Chip size="small" color="warning" variant="outlined" label="sandbox" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" color={s.status === 'IN_GRACE' ? 'warning' : 'success'} label={s.status ?? '—'} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color={s.status === 'IN_GRACE' ? 'warning.main' : undefined}>
                                            {s.status === 'IN_GRACE'
                                                ? `grace until ${formatDay(s.graceUntil)}`
                                                : `${s.shop === 'BETUP' ? 'until' : s.autoRenew ? 'renews' : 'ends'} ${formatDay(s.periodEnd)}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{formatDay(s.createdAt)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Open the player's Premium tab">
                                            <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/users/${s.userId}?tab=premium`); }}>
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {pages > 1 && (
                        <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} sx={{ alignSelf: 'center' }} />
                    )}
                </Stack>
            </CardContent>
            <SubscriptionEventsDialog subscriber={eventsOf} onClose={() => setEventsOf(null)} />
        </Card>
    );
}

/** One subscription's events (qbet_subscription_event), oldest first. */
function SubscriptionEventsDialog({ subscriber, onClose }: { subscriber: SubscriberDto | null; onClose: () => void }) {
    const [events, setEvents] = React.useState<SubscriptionEventItemDto[] | null>(null);
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        if (subscriber == null) return;
        setEvents(null);
        setFailed(false);
        getSubscriptionEvents(subscriber.subscriptionId).then(result => {
            setFailed(result == null);
            setEvents(result ?? []);
        });
    }, [subscriber]);

    return (
        <Dialog open={subscriber != null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="h6">
                        Subscription #{subscriber?.subscriptionId} · {subscriber?.userName ?? `#${subscriber?.userId}`}
                    </Typography>
                    <SubscriptionTierChip tier={subscriber?.tier} />
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {failed && <Alert severity="error">Failed to load the events.</Alert>}
                {events == null ? <CircularProgress /> : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Period end</TableCell>
                                <TableCell>Note</TableCell>
                                <TableCell>At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center">No events</TableCell></TableRow>
                            )}
                            {events.map(e => (
                                <TableRow key={e.id}>
                                    <TableCell>
                                        <Stack direction="row" gap={0.75} alignItems="center">
                                            {EVENT_ICON[e.type]}
                                            <Typography variant="body2">{e.type}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{formatPrice(e.priceMicros, e.currency)}</TableCell>
                                    <TableCell>{formatDay(e.periodEnd)}</TableCell>
                                    <TableCell>{e.note ?? '—'}</TableCell>
                                    <TableCell>{formatDateTime(e.createdAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
}
